import { AllProps, useComponentStyle } from '@altermaind/web/ui';
import { convertSizeToNumber } from '@/_shared/helpers';
import { useSubComponentStyle } from '@/_shared/hooks';

const DEFAULT_TRACK_WIDTH = 64;
const DEFAULT_TRACK_HEIGHT = 34;
const THUMB_SIZE_PROPORTION_TO_TRACK_HEIGHT = 0.8;

/**
 * useSwitchStyle
 *
 * Resolves the effective theme variant for the current checked state and
 * computes the thumb sliding distance. All visual styling lives in the theme
 * (`switch.ts`); this hook only derives the variant name and the geometry that
 * cannot be expressed declaratively in CSS.
 *
 * @param variant base variant carrying color + size, e.g. `classic`, `dark/small`
 * @param isChecked current toggle state — appends `/checked` to the variant
 * @param gap space (px) between the thumb and the track edges
 */
export const useSwitchStyle = (
  variant: string,
  isChecked: boolean,
  gap: number,
  userThumbSize: AllProps['size'],
) => {
  const effectiveVariant = isChecked ? `${variant}/checked` : variant;

  // Merged base + variant, used only to read the numeric geometry.
  const track = useComponentStyle('Switch', effectiveVariant);
  const { thumb } = useSubComponentStyle('Switch', effectiveVariant);

  const trackWidth = convertSizeToNumber(String(track.width)) || DEFAULT_TRACK_WIDTH;
  const trackHeight = convertSizeToNumber(String(track.height)) || DEFAULT_TRACK_HEIGHT;
  // Priority: User passed thumb size > theme thumb size > computed thumbSize with 80% trackHeight
  const thumbSize =
    convertSizeToNumber(String(userThumbSize)) ||
    convertSizeToNumber(String(thumb.height)) ||
    trackHeight * THUMB_SIZE_PROPORTION_TO_TRACK_HEIGHT;

  const translateX = isChecked ? trackWidth - thumbSize - gap : gap;

  return { effectiveVariant, translateX, track, thumb, thumbSize };
};
